import { BrowserRouter as Router } from "react-router-dom";
import { UserProvider, useUser } from "./contexts/UserContext";
import { HeartbeatContextProvider } from './components/features/heartbeat/HeartbeatContext';
import { heartbeatContextScope } from './components/features/heartbeat/useHeartbeatContext';
import { DataProvider } from "./contexts/DataContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { ScanProgressProvider } from "./contexts/ScanProgressContext";
import { AdminQueueProvider } from "./contexts/AdminQueueContext";
import AppRoutes from "./components/Routes";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { ToastProvider, ConfirmProvider } from './components/ui';
import RouteTitle from "./components/RouteTitle";

/**
 * App - Main application component
 * 
 * Simplified and clean main component that:
 * - Sets up all necessary context providers
 * - Wraps the application with BrowserRouter
 * - Delegates all routing logic to the centralized AppRoutes component
 * 
 * Benefits of the new structure:
 * - Clean separation of concerns
 * - Easy to maintain and modify routes
 * - Consistent route handling
 * - Centralized route configuration
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <UserProvider>
      <WebSocketProvider>
        <DataProvider>
          <ScanProgressProvider>
            <AdminQueueProvider>
              <ToastProvider>
                <ConfirmProvider>
                  <Router>
                    <RouteTitle />
                    <ContextScopedRoutes />
                  </Router>
                </ConfirmProvider>
              </ToastProvider>
            </AdminQueueProvider>
          </ScanProgressProvider>
        </DataProvider>
      </WebSocketProvider>
    </UserProvider>
    </QueryClientProvider>
  );
}

function ContextScopedRoutes() {
  const { user } = useUser();
  return <HeartbeatContextProvider scope={heartbeatContextScope(user)}><AppRoutes /></HeartbeatContextProvider>;
}

export default App;
